import * as React from 'react';
import { Plus } from 'lucide-react';

import VendorCategoryPill from '@/components/VendorCategoryPill';
import type { GmailContact } from '@/components/ContactField';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { matchesVendorQuery, primaryEmail, looksLikeEmail } from '@/lib/vendor-search';
import type { VendorOption } from '@/lib/vendor-search';

/**
 * The recipient field for an inquiry batch — the mail-client "To" line, with
 * the planner's rolodex behind it.
 *
 * Why this exists at all: the screen it replaces as the primary control is a
 * two-column checkbox grid over the whole rolodex. That grid is fine for
 * browsing and hopeless for a planner who already knows who she is mailing,
 * which is the common case. "Type three letters, press Enter, repeat" is the
 * interaction she already has in Gmail, and the one the ticket asks for.
 *
 * The grid is not deleted — it is one press away, in the side sheet the
 * composer opens from this field's own row (as `RolodexPicker`), for the browse
 * case. The two are deliberately two views of one list, which is why the rows
 * below copy the grid's row vocabulary exactly: business name, the address
 * beneath it in small muted text, an outlined category pill on the right. A
 * planner should not have to learn that these are different things.
 *
 * The list is two sources behind one dropdown (EVE-88): the rolodex, plus the
 * people the planner has emailed and never filed as vendors. Picking someone
 * from the second half opens the Add Vendor dialog prefilled with their name
 * and address — an inquiry is addressed to a vendor record, so a recipient has
 * to become one, and that is how "also add them to the rolodex" falls out of
 * addressing the letter rather than being a separate errand. The merged list
 * does not double up, because `searchContacts` leaves out every address already
 * in the rolodex — and the rolodex half of this list is the `vendors` prop the
 * field was already rendering. (It fetches neither half itself; both arrive as
 * props, which is what keeps one list's refetch from re-rendering the other.)
 *
 * Built on `ui/combobox.tsx` rather than hand-rolled, because the parts of this
 * that are easy to get wrong — chip semantics, arrow-key navigation, the
 * highlight/selection split, and the whole listbox ARIA contract — are what
 * base-ui's Combobox already ships.
 */

/**
 * A row in the dropdown. The "create a vendor" offer is a row in the *same*
 * array as the vendors, not a static element rendered outside the list, because
 * anything outside the list is invisible to keyboard navigation and to the
 * `filter` prop — which is exactly how you ship an affordance that only works
 * with a mouse. Past contacts are in the same array for the same reason.
 *
 * Three kinds, one of which is a recipient. A vendor row can be chipped
 * outright; a contact row and the create offer are both requests for a vendor
 * record, because there is no send-to-a-bare-address path on this screen.
 */
type Row =
  | { kind: 'vendor'; vendor: VendorOption }
  | { kind: 'contact'; contact: GmailContact }
  | { kind: 'create'; text: string };

/**
 * What a new vendor is being created *from*, in the two fields the dialog can
 * seed. An object rather than the string this used to be, so the caller is not
 * left sniffing the shape of some text a long way from where it was typed.
 */
export type VendorPrefill = { businessName?: string; email?: string };

/**
 * Makes a new `Row` kind a compile error in every place that has to handle one,
 * rather than a row that renders fine and then does nothing at all when
 * pressed — a silent failure with no type error and no failing test behind it.
 */
function assertNever(row: never): never {
  throw new Error(`Unhandled recipient row: ${JSON.stringify(row)}`);
}

/**
 * Item values here are objects, and base-ui compares selected values to item
 * values with `Object.is` by default. Rows are rebuilt on every keystroke, so
 * referential equality is never true for longer than one render — the controlled
 * value has to be matched by identity we define instead.
 */
function rowKey(row: Row): string {
  switch (row.kind) {
    case 'vendor':
      return `vendor:${row.vendor.id}`;
    case 'contact':
      // The address, which is unique per planner — a unique index on
      // (plannerId, email) is what makes it an identity rather than a guess.
      return `contact:${row.contact.email}`;
    case 'create':
      return `create:${row.text}`;
    default:
      return assertNever(row);
  }
}

/**
 * The selection base-ui just asked for, folded into the one the caller holds.
 *
 * Not simply `next`. `rows` only ever contains vendors this rolodex resolved,
 * so an id in `selected` that `vendors` does not answer for was never offered
 * to base-ui, and is missing from `next` for that reason alone — not because
 * the planner removed it. Rebuilding the selection from `next` would therefore
 * delete it as a side effect of removing some *other* chip: data loss caused by
 * an action aimed at a different vendor, with nothing on screen to show it
 * happened. This is not a hypothetical guard: the composer's own create path
 * reaches it. `AddVendorDialog` invalidates the `['vendors']` query without
 * awaiting it and then reports the new vendor synchronously, so the page pushes
 * an id into `selected` that this component cannot resolve until the refetch
 * lands — and the field is open for business for that whole window. Removing a
 * different chip in it is exactly the case the fold protects. This component
 * reports what the selection should be; it has no business pruning ids it
 * cannot see.
 *
 * Holding positions is the actual contract: `selected`'s order survives, and a
 * removal can therefore never reorder the batch. That is not quite the same as
 * "identical to `nextIds` whenever every id resolves" — given `selected`
 * `[A, B]` and `nextIds` `[B, A]` this returns `[A, B]`, deliberately refusing
 * the reorder. The two coincide only while `nextIds` keeps `selected`'s
 * relative order, which is what base-ui does today: it filters the value array
 * and appends to it, never permutes it.
 *
 * Which way this fails if the precondition ever breaks, since that is the whole
 * case for the branch. A preserved id that resolves to no vendor renders no
 * chip here and still occupies a slot against `max`, so this view does
 * under-report. It does not, however, get mailed: the composer renders it at
 * the review step as "Unknown vendor" with no address, and a missing address
 * is one of the conditions that blocks sending outright. So preserve fails
 * loudly one screen later, while drop destroys the id at the moment of a click
 * aimed at a different vendor and leaves nothing to notice. That asymmetry is
 * the reason for the fold, not a general claim that preserving is safer.
 */
function foldSelection(
  selected: string[],
  nextIds: string[],
  resolved: (id: string) => boolean,
): string[] {
  const keep = new Set(nextIds);
  const held = selected.filter((id) => !resolved(id) || keep.has(id));
  return [...held, ...nextIds.filter((id) => !held.includes(id))];
}

/**
 * Does this vendor already *answer to* exactly what was typed? Used only to
 * decide whether to offer creating a new one. A substring match is not enough:
 * typing "Bloom" when Bloom & Co exists should still let a planner add the
 * second, unrelated "Bloomsbury" — but typing a vendor's own address back at
 * the field should not offer to duplicate them.
 */
function answersExactly(vendor: VendorOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (vendor.businessName.trim().toLowerCase() === q) return true;
  return vendor.emails.some((e) => e.email.trim().toLowerCase() === q);
}

/**
 * The offer to create a vendor from whatever was typed.
 *
 * Not selectable: the press is intercepted in `onValueChange` and forwarded as
 * a request, because every recipient has to resolve to a vendor record.
 */
function CreateRow({
  row,
  disabled,
}: {
  row: Extract<Row, { kind: 'create' }>;
  disabled: boolean;
}) {
  return (
    <ComboboxItem value={row} disabled={disabled} className="items-start">
      <Plus className="mt-0.5 size-3.5 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">
          Add &ldquo;{row.text}&rdquo; as a vendor
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {looksLikeEmail(row.text)
            ? 'Nothing in your rolodex uses that address.'
            : 'No vendor in your rolodex answers to that.'}
        </span>
      </span>
    </ComboboxItem>
  );
}

/**
 * One past correspondent, in the vendor row's own three parts: the name, the
 * address beneath it in small muted text, and the slot on the right that a
 * vendor fills with its category pill.
 *
 * A contact has no category, and what belongs in that slot is the one thing
 * that separates this row from the one above it — she is not in the rolodex, so
 * pressing this opens a form instead of adding a chip. Said in the row's own
 * type scale and muted token rather than a second badge style, because these
 * are two views of one list and a planner should not have to learn that they
 * are different things.
 *
 * A contact with no display name is the address *only*, rather than a blank
 * line above it — half this list is bare addresses, and a column of empty first
 * lines is worse than a shorter row. Same rule as `ContactField`.
 *
 * Not selectable, like `CreateRow`: the press is intercepted in
 * `onValueChange` and forwarded as a request to create the vendor.
 */
function ContactRow({
  row,
  disabled,
}: {
  row: Extract<Row, { kind: 'contact' }>;
  disabled: boolean;
}) {
  const c = row.contact;
  return (
    <ComboboxItem value={row} disabled={disabled} className="items-start">
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{c.displayName ?? c.email}</span>
        {c.displayName !== null && (
          <span className="block truncate text-xs text-muted-foreground">{c.email}</span>
        )}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">Not in your rolodex yet</span>
    </ComboboxItem>
  );
}

/** One rolodex row, in the same three parts as a row of the browse grid. */
function VendorRow({
  row,
  reason,
  disabled,
  showCategory,
}: {
  row: Extract<Row, { kind: 'vendor' }>;
  /** Why this vendor cannot be picked, or null if it can. */
  reason: string | null;
  disabled: boolean;
  showCategory: boolean;
}) {
  const v = row.vendor;
  return (
    <ComboboxItem value={row} disabled={disabled} className="items-start">
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{v.businessName}</span>
        {/* Why the vendor cannot be picked displaces the address, rather than
            sitting next to it: the reason is the only thing she needs from this
            row, and it is the whole point of keeping unpickable vendors
            visible. Seeing nothing when you type "Bloom" is what makes you add
            a duplicate. */}
        <span className="block truncate text-xs text-muted-foreground">
          {reason ?? primaryEmail(v) ?? 'No email address on file'}
        </span>
      </span>
      {showCategory && <VendorCategoryPill category={v.primaryCategory} />}
    </ComboboxItem>
  );
}

/**
 * The name the field falls back to when the caller supplies none, so the two
 * places that need it cannot drift apart. The component is usable standalone,
 * so naming itself is the default rather than the caller's obligation.
 */
const FALLBACK_LABEL = 'Vendor recipients';

/**
 * Which of the two surfaces this field is drawn on.
 *
 * `field` is a standalone form control, dressed like every other input in the
 * app. `envelope` is the "To" line of the letter the composer previews, where a
 * boxed input glued into a rendered email reads as a form someone pasted into a
 * letter. A mail client's To line is not a box, so this variant has none.
 *
 * A prop rather than a `className` the caller passes in, because the two
 * treatments are not a tweak apart: the envelope one has to *un-set* the
 * border, fill, shadow and focus lift the control variant is built from, and
 * replace the focus indicator with one that works without a box to lift. A
 * caller handing in utilities would have to know which of those to cancel, and
 * the one that is easy to forget is the focus indicator.
 */
export type VendorRecipientFieldVariant = 'field' | 'envelope';

const CHIPS_BOX_CLASS: Record<VendorRecipientFieldVariant, string> = {
  // Pulled onto the app's own input treatment rather than the shadcn default
  // the primitive ships with, so this reads as the same control as every other
  // field on the page: the softer radius, the hairline outline token, and the
  // lift-on-focus instead of a ring. The `text-base md:text-sm` pair is
  // load-bearing rather than cosmetic — it is the 16px floor that stops iOS
  // Safari zooming the viewport when the field takes focus, and every other
  // field on this page already has it.
  field:
    'min-h-10 rounded-2xl border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-1.5 text-base shadow-none transition-[color,box-shadow,background-color] focus-within:border-[var(--color-outline-variant)] focus-within:bg-[var(--color-surface-bright)] focus-within:shadow-(--shadow-input-focus) focus-within:ring-0 has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip]:px-2 md:text-sm',

  // The letterhead's own vocabulary: no outline, no fill, no shadow — the
  // chips sit on the page of the letter next to the quiet "To" label the
  // caller renders, and the subject line above keeps its weight. The 16px
  // iOS-zoom floor stays, because it is about the keyboard and not the dress.
  //
  // Two states are drawn rather than inherited:
  //
  // *Hover* is a whisper of the next surface up (1.12:1 on the letter) — just
  // enough to answer "is this a control?" for someone who sees bare text where
  // every other To line they use is a box, without putting a box back.
  //
  // *Focus* is a real 2px sage outline, and it deliberately does **not** copy
  // `ui/input.tsx`. That control indicates focus by swapping
  // `--color-surface-container-lowest` for `--color-surface-bright` with the
  // border held and the ring switched off, which is a 1.03:1 change — under the
  // 3:1 WCAG 2.2 SC 2.4.11 asks of a focus indicator, and only legible at all
  // because the field it lifts is a box with an edge to catch the shadow. This
  // variant has no box, so inheriting that would have left the To line with no
  // focus indicator whatsoever. `--color-ring` measures 6.39:1 against the
  // letter and 5.39:1 against a chip's champagne fill, and it is the same
  // indicator the chips inside already use for their own focus, so the field
  // and its contents agree about what focus looks like. An `outline` rather
  // than a `ring`, because a ring is a box-shadow and forced-colors mode wipes
  // box-shadows — an outline is the one focus indicator that survives it.
  envelope:
    'min-h-7 rounded-lg border-transparent bg-transparent px-1.5 py-0.5 text-base shadow-none transition-[background-color,outline-color] hover:bg-[var(--color-surface-container-low)] focus-within:border-transparent focus-within:bg-transparent focus-within:shadow-none focus-within:ring-0 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring has-disabled:cursor-not-allowed has-disabled:opacity-50 md:text-sm',
};

export default function VendorRecipientField({
  vendors,
  contacts,
  pending,
  selected,
  onChange,
  query,
  onQueryChange,
  category,
  disabledReason,
  max,
  onCreateRequest,
  variant = 'field',
  id,
  'aria-labelledby': labelledBy,
  'aria-describedby': describedBy,
}: {
  vendors: VendorOption[] | undefined;
  /**
   * People the planner has emailed who are not vendors yet, already ranked and
   * narrowed to what she typed by the server.
   *
   * A prop rather than a query in here, following `vendors`: this component
   * fetches nothing, and the page above owns both lists — it is also the one
   * place that can keep the request from firing before anything is typed.
   *
   * `undefined` covers "not asked for" and "the request failed" alike, which
   * are the same thing to this list: no suggestions, and nothing said about it.
   */
  contacts: GmailContact[] | undefined;
  /** Query still in flight. `undefined` vendors also covers a failed fetch,
   *  which must not look like an empty rolodex. */
  pending: boolean;
  selected: string[];
  onChange: (next: string[]) => void;
  query: string;
  onQueryChange: (next: string) => void;
  category: string;
  /**
   * Why this vendor cannot be picked, or null if it can.
   *
   * Takes the vendor rather than its id because every caller already holds
   * one: passing the id made this component and the grid beside it look the
   * vendor up again per rendered row, and put `vendors` in the dependencies of
   * the caller's callback, so every rolodex refetch invalidated it and
   * re-rendered both lists.
   */
  disabledReason: (vendor: VendorOption) => string | null;
  max: number;
  /**
   * A recipient was asked for that is not a vendor yet — either nothing in the
   * rolodex answered to what she typed, or she picked someone she has emailed
   * and never filed.
   *
   * One path for both, deliberately: the dialog behind it is the same dialog,
   * so the duplicate check and the required fields cannot drift between the two
   * ways in. It carries the halves already separated, so the caller does not
   * have to work out which field a bare string belongs in.
   */
  onCreateRequest: (prefill: VendorPrefill) => void;
  /**
   * Which surface this is drawn on — see `VendorRecipientFieldVariant`.
   * Defaults to the standalone control, so a caller who has not thought about
   * it gets the form field rather than an unboxed one on a page with no
   * letterhead to sit on.
   */
  variant?: VendorRecipientFieldVariant;
  /**
   * Lands on the text input, so a caller can write `<Label htmlFor>` — which is
   * what every other field in the composer uses. Without this passthrough it is
   * not merely awkward but impossible: base-ui generates the input's id and
   * nothing outside the component can know it.
   */
  id?: string;
  /**
   * Names the field from an element the caller already renders, and switches
   * off the built-in name. Both halves matter: `aria-label` outranks a visible
   * `<label for>`, so without this a visible "To" would be announced as
   * "Vendor recipients".
   */
  'aria-labelledby'?: string;
  /**
   * An extra description to hang on the input — the recipient counter, in the
   * composer's case.
   *
   * Merged with the field's own cap notice rather than replacing it. Both
   * matter, and the cap notice is the one that fires at the moment picking
   * stops working, so it is not the half to lose.
   */
  'aria-describedby'?: string;
}) {
  // The popup is anchored to the whole chips box rather than to the input,
  // which is a caret-width element wandering around inside it. Anchoring to the
  // caret would make the menu jump sideways as chips wrap onto a second line.
  const anchor = useComboboxAnchor();
  const capNoticeId = React.useId();

  const all = React.useMemo(() => vendors ?? [], [vendors]);
  const allContacts = React.useMemo(() => contacts ?? [], [contacts]);
  const byId = React.useMemo(() => new Map(all.map((v) => [v.id, v])), [all]);

  // Chip order has to be the selection order, not the rolodex order: base-ui's
  // `ChipRemove` resolves which value to drop from its chip's *position*, so a
  // chips list that disagrees with the value array removes the wrong recipient.
  const selectedVendors = React.useMemo(
    () => selected.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : [])),
    [selected, byId],
  );
  const selectedRows = React.useMemo<Row[]>(
    () => selectedVendors.map((vendor) => ({ kind: 'vendor', vendor })),
    [selectedVendors],
  );

  const trimmed = query.trim();
  const atCap = selected.length >= max;

  // Caller's description first, then the field's own cap notice — assistive
  // tech reads the list in order, and the standing "3 of 25 max" is context for
  // the urgent "remove one to add another" rather than the other way round.
  // The cap notice is only referenced while it has something to say; the
  // `|| undefined` is what keeps an empty string from reaching the DOM as a
  // describedby pointing at nothing.
  const mergedDescribedBy =
    [describedBy, atCap ? capNoticeId : null].filter((v): v is string => !!v).join(' ') ||
    undefined;

  const rows = React.useMemo<Row[]>(() => {
    // Empty and focused, the field opens on the category being inquired about:
    // on a Florist batch, the florists. Anything else would open on a list of
    // hundreds sorted by nothing, which is not a starting point.
    //
    // Deliberately a narrowed *pool* rather than a filter the planner has to
    // find and clear — the way to see the rest of the rolodex is to type, which
    // is the thing she was going to do anyway. Already-picked vendors stay in
    // the pool regardless of their category so that a recipient already on the
    // batch can always be found and toggled back off.
    const pool = trimmed
      ? all
      : all.filter((v) => v.primaryCategory === category || selected.includes(v.id));

    // Stable sort, so "first" means first without reordering anything else.
    const sorted = [...pool].sort(
      (a, b) =>
        (a.primaryCategory === category ? 0 : 1) - (b.primaryCategory === category ? 0 : 1),
    );

    const next: Row[] = sorted.map((vendor) => ({ kind: 'vendor', vendor }));

    // Then the people she has emailed and never filed — but only once
    // something is typed, which is the one way this half differs from the
    // rolodex above it. An empty query is answered by the top of a ranking by
    // how often she mails an address, and on a real account that is her
    // family, her accountant and Send-to-Kindle rather than vendors. Clicking
    // in to browse has to stay a list of vendors.
    //
    // Under the vendor rows because those can be addressed outright, and above
    // the create offer because a contact is a *named* way to make a vendor and
    // the offer is the fallback when nothing answered at all.
    if (trimmed) {
      for (const contact of allContacts) next.push({ kind: 'contact', contact });
    }

    // The offer to create only makes sense once something has been typed, and
    // only while nothing already answers to it precisely. Last, for the same
    // reason it is called a fallback.
    if (trimmed && !all.some((v) => answersExactly(v, trimmed))) {
      next.push({ kind: 'create', text: trimmed });
    }
    return next;
  }, [all, allContacts, trimmed, category, selected]);

  // Only while the list can span categories. Under the empty-query view every
  // row carries the same word, which is a column of noise rather than a way to
  // scan — the same reason the grid hides the pill under an active filter.
  const showCategoryPill = trimmed !== '';

  return (
    <div className="space-y-1.5">
      <Combobox
        multiple
        items={rows}
        value={selectedRows}
        onValueChange={(next: Row[], details) => {
          // Partitioned rather than filtered, so that a new `Row` kind has to
          // be answered here too instead of being quietly discarded.
          const nextIds: string[] = [];
          let prefill: VendorPrefill | null = null;
          for (const row of next) {
            switch (row.kind) {
              case 'vendor':
                nextIds.push(row.vendor.id);
                break;
              case 'contact':
                // Both halves, which is the whole convenience: she never goes
                // and looks the address up. A contact with no display name
                // carries none rather than a guess from the local part.
                prefill = {
                  businessName: row.contact.displayName ?? undefined,
                  email: row.contact.email,
                };
                break;
              case 'create':
                // The sniff lives here, where the text actually is: an
                // email-shaped string is an address, anything else is a
                // business name.
                prefill = looksLikeEmail(row.text)
                  ? { email: row.text }
                  : { businessName: row.text };
                break;
              default:
                assertNever(row);
            }
          }

          // Accepting a row is the one gesture that should clear the query:
          // type, accept, repeat is the core loop, and the text has done its
          // job once a chip exists. It happens here, where the distinction is
          // an ordinary documented argument, because `onInputValueChange`
          // below cannot tell an accept from a dismissal — base-ui reports
          // both as `input-clear`.
          //
          // `item-press` is every accept: pointer, Enter on a highlighted row,
          // toggling a selected row back off, and pressing the create offer.
          // The other reasons that reach this callback leave the text alone,
          // which is right for a gesture aimed at a chip rather than the list:
          // `chip-remove-press` from the X button, `none` from Backspace or
          // Delete on a focused chip — `ComboboxChip` raises its own removal
          // rather than inheriting the X button's reason — and `clear-press`
          // if a `ComboboxClear` is ever added to this field.
          if (details.reason === 'item-press') {
            onQueryChange('');
          }

          // The create row and the contact rows are actions wearing an
          // option's clothes. Every recipient on this batch has to resolve to a
          // vendor record — there is no "send to a bare address" path — so the
          // press is forwarded as a request and the selection is left exactly
          // as it was. `value` is controlled, so simply not calling `onChange`
          // is enough to refuse it. The caller's dialog is what turns the
          // request into a vendor, and its `onCreated` is what finally puts the
          // recipient on the batch.
          if (prefill !== null) {
            onCreateRequest(prefill);
            return;
          }
          onChange(foldSelection(selected, nextIds, (vendorId) => byId.has(vendorId)));
        }}
        inputValue={query}
        onInputValueChange={(next, details) => {
          // The whole point of this component is that it is the only text input
          // on the step: what the planner types narrows this dropdown *and* the
          // rolodex grid behind it, which has no search box of its own any more.
          // base-ui wipes a controlled input on every close, which would undo
          // that at the worst possible moment — she dismisses the dropdown
          // precisely in order to reach the grid, and the grid would widen back
          // out as she did it.
          //
          // So every close-driven wipe is refused. All of them arrive as
          // `input-clear`, and in this configuration nothing else does: typing
          // reports `input-change`, so the planner can still empty the field
          // herself, and a `ComboboxClear` would report `clear-press`. The
          // accept path's clear is issued from `onValueChange` above instead,
          // which is what makes this pair independent of the order base-ui
          // happens to fire the two callbacks in — whichever lands first, the
          // query ends up empty on an accept and unchanged on a dismissal.
          //
          // Refusing it also closes a mis-click window. Outside-press dismisses
          // on `pointerdown`, so the old behaviour reflowed the grid between the
          // planner's press and her `click` — she could aim at one row and tick
          // whichever one slid under the cursor. Keeping the query keeps the
          // grid still.
          if (details.reason === 'input-clear') {
            // base-ui's own documented opt-out, checked immediately after
            // this callback returns. Preferred over simply declining to
            // propagate because it states the refusal rather than leaning on
            // the side effect of a controlled prop not being reassigned. It
            // also skips base-ui's internal uncontrolled-value write, which is
            // inert while `inputValue` is controlled but would be the live
            // value if this field were ever uncontrolled.
            details.cancel();
            return;
          }
          onQueryChange(next);
        }}
        itemToStringLabel={(row: Row) => {
          switch (row.kind) {
            case 'vendor':
              return row.vendor.businessName;
            case 'contact':
              return row.contact.displayName ?? row.contact.email;
            case 'create':
              return row.text;
            default:
              return assertNever(row);
          }
        }}
        isItemEqualToValue={(a: Row, b: Row) => rowKey(a) === rowKey(b)}
        // Matching lives in `lib/vendor-search` because the browse grid this
        // field filters has to narrow by identical rules — a vendor findable in one view
        // and not the other is the bug this whole screen is fixing.
        filter={(row: Row, q: string) => {
          switch (row.kind) {
            case 'vendor':
              return matchesVendorQuery(row.vendor, q);
            // Already matched, by rules this side does not have: the server
            // narrowed these in Postgres and ranked them by how often and how
            // recently she mailed each address. Narrowing them again here would
            // be a second set of rules, and the two would drift.
            case 'contact':
              return true;
            case 'create':
              return true;
            default:
              return assertNever(row);
          }
        }}
        // Gated on `!vendors` as well as `pending`, because `undefined` means
        // both "in flight" and "the request failed" — the same reason the grid
        // in the sheet branches on `pending || !vendors`. Left enabled on a
        // failed fetch, this field answers a typed vendor name with "no vendor
        // in your rolodex answers to that" and offers to create a duplicate of
        // someone who does exist. Shut is the only honest state.
        disabled={pending || !vendors}
      >
        <ComboboxChips
          ref={anchor}
          // base-ui only gives this box `role="toolbar"` once it holds chips —
          // the role is what keeps NVDA in focus mode while arrow-keying
          // between them — so the name is conditional on the same thing. ARIA
          // prohibits naming the role-less div it is the rest of the time, and
          // an unnamed toolbar is just as bad in the other direction.
          aria-labelledby={selectedVendors.length > 0 ? labelledBy : undefined}
          aria-label={
            selectedVendors.length > 0 && !labelledBy ? FALLBACK_LABEL : undefined
          }
          // Both treatments, and why they differ, are at `CHIPS_BOX_CLASS`.
          className={CHIPS_BOX_CLASS[variant]}
        >
          {selectedVendors.map((v) => (
            <ComboboxChip
              key={v.id}
              removeLabel={`Remove ${v.businessName}`}
              // The stock chip is `w-fit whitespace-nowrap` with nothing to
              // stop it: a flex item that cannot wrap and cannot shrink. At
              // phone width "Ava Lane Floral Design & Event Styling Studio" is
              // wider than the field, and the overflow pushes a scrollbar onto
              // the whole page rather than staying inside the control.
              className="max-w-full min-w-0"
            >
              {/* Truncating the name rather than the chip, so the remove button
                  stays put at the end instead of being pushed out of reach. The
                  full name is still announced — `removeLabel` carries it. */}
              <span className="truncate">{v.businessName}</span>
            </ComboboxChip>
          ))}
          <ComboboxChipsInput
            id={id}
            aria-labelledby={labelledBy}
            aria-label={labelledBy ? undefined : FALLBACK_LABEL}
            aria-describedby={mergedDescribedBy}
            className="placeholder:text-muted-foreground"
            placeholder={
              pending
                ? 'Loading your rolodex…'
                : !vendors
                  ? 'Couldn’t load your rolodex — reload to try again.'
                  : selected.length
                    ? ''
                    : 'Type a vendor name or email…'
            }
          />
        </ComboboxChips>

        <ComboboxContent anchor={anchor}>
          <ComboboxList>
            {(row: Row) => {
              switch (row.kind) {
                case 'create':
                  // Greyed at the cap along with everything else. A live offer
                  // to add a 26th vendor sitting directly under a notice that
                  // says "remove one to add another" is the list contradicting
                  // itself; and it stays in place rather than disappearing for
                  // the same reason unpickable vendors do — a row that vanishes
                  // reads as "not possible here", a greyed one reads as "not
                  // yet", which is the truth.
                  return <CreateRow key={rowKey(row)} row={row} disabled={atCap} />;
                case 'contact':
                  // Greyed at the cap alongside the create offer, and for the
                  // same reason: pressing one ends in a new recipient, which
                  // is the thing the cap refuses.
                  return <ContactRow key={rowKey(row)} row={row} disabled={atCap} />;
                case 'vendor': {
                  const reason = disabledReason(row.vendor);
                  const isSelected = selected.includes(row.vendor.id);
                  return (
                    <VendorRow
                      key={rowKey(row)}
                      row={row}
                      reason={reason}
                      // At the cap, picked rows stay live. The planner's next
                      // move is almost always to swap one recipient for
                      // another, and a list where nothing responds gives her no
                      // way to do it from here.
                      disabled={reason !== null || (atCap && !isSelected)}
                      showCategory={showCategoryPill}
                    />
                  );
                }
                default:
                  return assertNever(row);
              }
            }}
          </ComboboxList>
          {/* The only way the list comes back empty: nothing typed, and no
              vendor filed under the category being inquired about. Once
              something is typed there is always at least the offer to create,
              so there is no "no results" state to write. */}
          <ComboboxEmpty>
            No {category} vendors yet — type a name or email to search your whole rolodex.
          </ComboboxEmpty>
        </ComboboxContent>
      </Combobox>

      {/* Named only once it bites. A permanent "25 max" above an empty field is
          a rule nobody has run into yet; at the cap it is the reason the next
          vendor would not go in, so it states the limit and the way out.

          The announcement is the point: this notice always arrives while focus
          is already inside the input, where the `aria-describedby` pointing at
          it is never re-read, so without a live region a screen-reader user
          gets no signal at all that the vendor was refused.

          Which is why the element is mounted at all times and hidden with
          `sr-only` rather than conditionally rendered or `display:none`'d. A
          `display:none` subtree is absent from the accessibility tree
          altogether, so hiding it that way is indistinguishable from never
          rendering it — the region would both appear and gain its text in one
          commit, which is the newly-populated-region case screen readers
          announce least reliably. `sr-only` keeps it continuously present and
          continuously a live region, so what changes is only its text, which is
          exactly the mutation `role="status"` exists to report. It also costs
          no layout: `sr-only` takes the empty paragraph out of flow, so the
          wrapper's `space-y-1.5` has nothing to put a gap above. */}
      <p
        id={capNoticeId}
        role="status"
        // `aria-live` is not the redundant twin of `role="status"` it looks
        // like, and deleting it silently breaks the announcement. While the
        // dropdown is open base-ui hides the rest of the page from assistive
        // tech, and the exemption it grants live regions is written as the
        // attribute selector `[aria-live]` — which `role="status"` does not
        // match, however much it implies the same thing. Without the attribute
        // this notice is `aria-hidden` for exactly as long as the dropdown is
        // open, which is precisely when the planner hits the cap.
        aria-live="polite"
        className={atCap ? 'text-xs text-muted-foreground' : 'sr-only'}
      >
        {atCap
          ? `Maximum ${max} ${max === 1 ? 'vendor' : 'vendors'} at a time. Remove one to add another.`
          : null}
      </p>
    </div>
  );
}
